/**
 * Help Articles - Hook for managing article state and API calls
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useHelpArticles = () => {
  const { toast } = useToast();
  
  const [sections, setSections] = useState([]);
  const [versionLogs, setVersionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    mobile_content: '',
    status: 'draft',
    tags: []
  });

  const headers = {
    'Content-Type': 'application/json'
  };

  // Fetch articles
  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/help/admin`,
        { headers, withCredentials: true }
      );
      setSections(response.data.sections || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch help articles'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch version logs
  const fetchVersionLogs = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/help/logs`,
        { headers, withCredentials: true }
      );
      // Combine edit_logs and audit_logs into a single list
      const editLogs = response.data.edit_logs || [];
      const auditLogs = response.data.audit_logs || [];
      const combinedLogs = [...editLogs, ...auditLogs].map(log => ({
        id: log.id,
        article_title: log.old_data?.title || log.section_id || 'Unknown',
        action: log.action || log.event || 'edit',
        admin_name: log.admin_username || log.actor_id || 'Admin',
        timestamp: log.timestamp,
        changes: log.action === 'edit' ? 'Content updated' : log.details || ''
      })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setVersionLogs(combinedLogs);
    } catch (error) {
      console.error('Failed to fetch version logs:', error);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
    fetchVersionLogs();
  }, [fetchArticles, fetchVersionLogs]);

  // Start editing
  const startEdit = useCallback((section) => {
    setEditingId(section.id);
    setEditForm({
      title: section.title,
      content: section.content,
      mobile_content: section.mobile_content || section.content,
      status: section.status,
      tags: section.tags || []
    });
  }, []);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditForm({
      title: '',
      content: '',
      mobile_content: '',
      status: 'draft',
      tags: []
    });
  }, []);

  // Save article
  const saveArticle = useCallback(async (sectionId) => {
    try {
      await axios.put(
        `${API_URL}/api/help/${sectionId}`,
        editForm,
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: 'Article updated successfully'
      });
      
      cancelEdit();
      fetchArticles();
      fetchVersionLogs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update article'
      });
    }
  }, [editForm, toast, cancelEdit, fetchArticles, fetchVersionLogs]);

  // Create new article - Backend doesn't support POST, show appropriate message
  const createArticle = useCallback(async (articleData) => {
    toast({
      variant: 'destructive',
      title: 'Not Available',
      description: 'Article creation is not yet implemented in the backend API. Please contact the development team.'
    });
    return false;
  }, [toast]);

  // Delete article - Backend doesn't support DELETE, show appropriate message
  const deleteArticle = useCallback(async (sectionId) => {
    toast({
      variant: 'destructive',
      title: 'Not Available',
      description: 'Article deletion is not yet implemented in the backend API. Please contact the development team.'
    });
  }, [toast]);

  // Toggle publish status - Use PUT to update status
  const togglePublish = useCallback(async (section) => {
    const newStatus = section.status === 'published' ? 'draft' : 'published';
    
    try {
      await axios.put(
        `${API_URL}/api/help/${section.id}`,
        { status: newStatus },
        { headers, withCredentials: true }
      );
      
      toast({
        title: 'Success',
        description: `Article ${newStatus === 'published' ? 'published' : 'unpublished'}`
      });
      
      fetchArticles();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update article status'
      });
    }
  }, [toast, fetchArticles]);

  // Reorder articles - Use PUT to update order
  const reorderArticles = useCallback(async (sectionId, direction) => {
    // Get current section and find its order
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const targetSection = sections[newIndex];
    
    try {
      // Swap orders using PUT
      await axios.put(
        `${API_URL}/api/help/${sectionId}`,
        { order: targetSection.order },
        { headers, withCredentials: true }
      );
      
      await axios.put(
        `${API_URL}/api/help/${targetSection.id}`,
        { order: section.order },
        { headers, withCredentials: true }
      );
      
      fetchArticles();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reorder article'
      });
    }
  }, [sections, toast, fetchArticles]);

  return {
    sections,
    versionLogs,
    loading,
    editingId,
    editForm,
    setEditForm,
    startEdit,
    cancelEdit,
    saveArticle,
    createArticle,
    deleteArticle,
    togglePublish,
    reorderArticles,
    fetchArticles
  };
};

export default useHelpArticles;
